import type { BanxaPaymentMethod, BanxaPrice } from '../services/apiService';

export interface BanxaPaymentMethodDisplay {
  label: string;
  icon: string;
}

/**
 * Stable display labels for Banxa v2 payment method ids. Falls back to API name.
 */
export function getBanxaPaymentMethodDisplay(method: {
  id: string;
  name?: string | null;
}): BanxaPaymentMethodDisplay {
  switch (method.id) {
    case 'payid-bank-transfer':
      return { label: 'PayID / Transfer', icon: '🏦' };
    case 'debit-credit-card':
      return { label: 'Debit / CC', icon: '💳' };
    case 'apple-pay':
      return { label: 'Apple Pay', icon: '🍎' };
    case 'google-pay':
      return { label: 'Google Pay', icon: '📲' };
    default:
      return {
        label: (method.name && method.name.trim()) || method.id,
        icon: '💳',
      };
  }
}

/**
 * Prefer the payment method with the lowest total fees for the given fiat amount quote.
 */
export function pickDefaultPaymentMethodForAmount(
  paymentMethods: BanxaPaymentMethod[],
  prices: BanxaPrice[] | undefined,
  fallbackFirst = true
): BanxaPaymentMethod | null {
  if (!paymentMethods.length) {
    return null;
  }
  if (!prices?.length) {
    return fallbackFirst ? paymentMethods[0] : null;
  }
  let bestPrice: BanxaPrice | undefined;
  let bestSum = Infinity;
  for (const p of prices) {
    const sum = (p.processingFee ?? 0) + (p.networkFee ?? 0);
    if (sum < bestSum) {
      bestSum = sum;
      bestPrice = p;
    }
  }
  if (!bestPrice) {
    return fallbackFirst ? paymentMethods[0] : null;
  }
  const match = paymentMethods.find((m) => m.id === bestPrice!.paymentMethodId);
  return match ?? (fallbackFirst ? paymentMethods[0] : null);
}
